import type { FC, KeyboardEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import type { Maybe } from '@/shared/libs';
import { cn } from '@/shared/libs/utils';

import { FieldError, Label } from '../../primitives';

export interface FormTagsInputOption {
  icon?: ReactNode;
  label: string;
  value: string;
}

export interface FormTagsInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  error?: string | string[];
  placeholder?: string;
  disabled?: boolean;
  max?: number;
  getRejectMessage?: (value: string) => string;
  options?: FormTagsInputOption[];
  restrict?: boolean;
}

const SUGGESTIONS_VISIBLE_LIMIT = 40;

export const FormTagsInput: FC<FormTagsInputProps> = ({
  disabled = false,
  error,
  getRejectMessage,
  label,
  max,
  onChange,
  options,
  placeholder = label,
  restrict = false,
  value = [],
}) => {
  const [inputValue, setInputValue] = useState('');
  const [rejectText, setRejectText] = useState<Maybe<string>>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const rejectTimerRef = useRef<Maybe<ReturnType<typeof setTimeout>>>(null);

  useEffect(
    () => () => {
      if (rejectTimerRef.current) {
        clearTimeout(rejectTimerRef.current);
      }
    },
    [],
  );

  const errorMessage = Array.isArray(error) ? error[0] : error;
  const hasError = !!errorMessage;
  const optionsByValue = useMemo(() => {
    const map = new Map<string, FormTagsInputOption>();

    if (options)
      for (const option of options) {
        map.set(option.value, option);
      }

    return map;
  }, [options]);

  const availableSuggestions = useMemo(() => {
    if (!options?.length) {
      return [];
    }

    const query = inputValue.trim().toLowerCase();

    return options
      .filter((option) => !value.includes(option.value))
      .filter(
        (option) =>
          !query ||
          option.label.toLowerCase().includes(query) ||
          option.value.toLowerCase().includes(query),
      )
      .slice(0, SUGGESTIONS_VISIBLE_LIMIT);
  }, [inputValue, options, value]);

  const showReject = (message: string): void => {
    if (rejectTimerRef.current) {
      clearTimeout(rejectTimerRef.current);
    }

    setRejectText(message);
    rejectTimerRef.current = setTimeout(() => {
      setRejectText(null);
      rejectTimerRef.current = null;
    }, 3000);
  };

  const resolveOption = (raw: string): Maybe<FormTagsInputOption> => {
    const query = raw.trim().toLowerCase();

    if (!query || !options?.length) {
      return null;
    }

    return (
      options.find(
        (option) =>
          option.value.toLowerCase() === query ||
          option.label.toLowerCase() === query,
      ) ?? null
    );
  };

  const addTag = (raw: string): void => {
    const tag = raw.trim();

    if (!tag) {
      return;
    }

    if (max !== undefined && value.length >= max) {
      return;
    }

    if (restrict) {
      const matched = resolveOption(tag);

      if (!matched) {
        if (getRejectMessage) {
          showReject(getRejectMessage(tag));
        }

        return;
      }

      if (value.includes(matched.value)) {
        setInputValue('');

        return;
      }

      onChange([...value, matched.value]);
      setInputValue('');
      setRejectText(null);

      return;
    }

    if (value.includes(tag)) {
      setInputValue('');

      return;
    }

    onChange([...value, tag]);
    setInputValue('');
    setRejectText(null);
  };

  const addOption = (option: FormTagsInputOption): void => {
    if (disabled || value.includes(option.value)) {
      return;
    }

    if (max !== undefined && value.length >= max) {
      return;
    }

    onChange([...value, option.value]);
    setRejectText(null);
  };

  const removeTag = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addTag(inputValue);
    } else if (
      event.key === 'Backspace' &&
      inputValue === '' &&
      value.length > 0
    ) {
      removeTag(value.length - 1);
    }
  };

  const handleBlur = (): void => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  };

  const tagContent = (tag: string): ReactNode => {
    const option = optionsByValue.get(tag);

    return (
      <span className="inline-flex max-w-[160px] items-center gap-1">
        {option?.icon}
        <span className="truncate">{option?.label ?? tag}</span>
      </span>
    );
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      {!!label && <Label>{label}</Label>}
      {availableSuggestions.length > 0 && (
        <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
          {availableSuggestions.map((option) => (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              className={cn(
                'cursor-pointer rounded-full bg-foreground/5 px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors',
                'hover:bg-indigo-500/10 hover:text-indigo-600 dark:hover:text-indigo-300',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              onClick={() => addOption(option)}>
              <span className="inline-flex items-center gap-1">
                + {option.icon}
                {option.label}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <div
          role="presentation"
          className={cn(
            'min-h-10 w-full cursor-text rounded-xl border border-border/60 bg-background/60 px-3 py-2 text-sm',
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500/40',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'transition-colors',
            hasError && 'border-destructive focus-within:ring-destructive',
          )}
          onClick={() => inputRef.current?.focus()}>
          <div className="flex flex-wrap items-center gap-1.5">
            {value.map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2 py-0.5 text-xs font-medium text-indigo-600 dark:text-indigo-300">
                {tagContent(tag)}
                {!disabled && (
                  <button
                    type="button"
                    disabled={disabled}
                    className="cursor-pointer rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeTag(index);
                    }}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {!disabled && (
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                placeholder={value.length === 0 ? placeholder : ''}
                disabled={
                  disabled || (max !== undefined && value.length >= max)
                }
                className={cn(
                  'min-w-[120px] flex-1 cursor-text border-none bg-transparent outline-none placeholder:text-muted-foreground',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
                onBlur={handleBlur}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={handleKeyDown}
              />
            )}
          </div>
        </div>
      </div>
      {!!rejectText && (
        <p className="text-[11px] text-destructive">{rejectText}</p>
      )}
      <FieldError error={error} />
    </div>
  );
};

FormTagsInput.displayName = 'FormTagsInput';
